import React, { useState, useEffect, useRef } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { FormattedMessage, useIntl } from '../../../../util/reactIntl';
import { NamedLink, PrimaryButton } from '../../../../components';
import NamedLinkNewTab from '../../../../components/NamedLink/NamedLinkNewTab';
import AvatarMedium from '../../../../components/Avatar/Avatar';
import ReviewRatings from '../ReviewRatings/ReviewRatings';
import { getListingReviewStats } from '../reviewStats';
import Modal from '../Utils/Modal/Modal';
import { HeartIcon, FacebookIcon, InstagramIcon, WhatsAppBrandIcon, StarIcon } from '../icons';
import { continentsList, continentsListDe } from '../../../../data/countries-list';
import css from './ListingHeader.module.css';

const pinIcon =
  'https://res.cloudinary.com/dlcacck3a/image/upload/v1769082864/socialbnb/assets/Pin_Icon.svg';

const findContinent = (countryName, lang) => {
  if (!countryName) return null;
  const continents = lang === 'de' ? continentsListDe : continentsList;
  return (
    Object.values(continents).find(continent =>
      continent.countries.some(c => c.nameEn === countryName || c.nameDe === countryName)
    ) || null
  );
};

const getExperienceTitle = (experienceType, intl) => {
  const map = {
    '2 Days 1 Night': 'experienceTitle.2d1n',
    '3 Days 2 Nights': 'experienceTitle.3d2n',
    '4 Days 3 Nights': 'experienceTitle.4d3n',
    '5 Days 4 Nights': 'experienceTitle.5d4n',
    '6 Days 5 Nights': 'experienceTitle.6d5n',
    '7 Days 6 Nights': 'experienceTitle.1w',
    '8 Days 7 Nights': 'experienceTitle.8d7n',
    '9 Days 8 Nights': 'experienceTitle.9d8n',
    '10 Days 9 Nights': 'experienceTitle.10d9n',
    '11 Days 10 Nights': 'experienceTitle.11d10n',
    '12 Days 11 Nights': 'experienceTitle.12d11n',
    '13 Days 12 Nights': 'experienceTitle.13d12n',
    '14 Days 13 Nights': 'experienceTitle.2w',
    '15 Days 14 Nights': 'experienceTitle.15d14n',
    '16 Days 15 Nights': 'experienceTitle.16d15n',
    '17 Days 16 Nights': 'experienceTitle.17d16n',
    '18 Days 17 Nights': 'experienceTitle.18d17n',
    '19 Days 18 Nights': 'experienceTitle.19d18n',
    '20 Days 19 Nights': 'experienceTitle.20d19n',
    '21 Days 20 Nights': 'experienceTitle.3w',
    '28 Days 27 Nights': 'experienceTitle.4w',
    '42 Days 41 Nights': 'experienceTitle.42d41n',
    '1 Week': 'experienceTitle.1w',
    '2 Weeks': 'experienceTitle.2w',
    '3 Weeks': 'experienceTitle.3w',
    '4 Weeks': 'experienceTitle.4w',
  };
  const key = map[experienceType];
  return key ? intl.formatMessage({ id: key }) : experienceType;
};

const formatDurationLabel = duration => {
  if (!duration) return null;
  const parts = duration.trim().split(/\s+/);
  if (parts.length >= 4) {
    return `${parts[0]} ${parts[1]} · ${parts[2]} ${parts[3]}`;
  }
  return duration;
};

const getTagline = (currentListing, isExperienceType, currentLanguage) => {
  const publicData = currentListing?.attributes?.publicData || {};
  const description = currentListing?.attributes?.description || '';

  if (isExperienceType) {
    const subtitle =
      currentLanguage === 'en'
        ? publicData.experienceSubtitle || publicData.experienceSubtitleEn
        : publicData.experienceSubtitleDe || publicData.experienceSubtitle;
    if (subtitle) return subtitle;
  } else {
    const staySubtitle =
      currentLanguage === 'en'
        ? publicData.staySubtitle || publicData.stayTitle
        : publicData.staySubtitleDe || publicData.stayTitleDe || publicData.stayTitle;
    if (staySubtitle) return staySubtitle;
  }

  if (!description) return null;
  const firstLine = description.split('\n').find(line => line.trim());
  if (!firstLine) return null;
  return firstLine.length > 140 ? `${firstLine.slice(0, 137)}…` : firstLine;
};

const ShareIcon = () => (
  <svg
    className={css.actionBtnIcon}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.9"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <circle cx="18" cy="5" r="3" />
    <circle cx="6" cy="12" r="3" />
    <circle cx="18" cy="19" r="3" />
    <path d="m8.6 13.5 6.8 4" />
    <path d="m15.4 6.5-6.8 4" />
  </svg>
);

const SHARE_OPTIONS = [
  {
    id: 'ig',
    label: 'Instagram',
    Icon: InstagramIcon,
  },
  {
    id: 'fb',
    label: 'Facebook',
    Icon: FacebookIcon,
  },
  {
    id: 'wa',
    label: 'WhatsApp',
    Icon: WhatsAppBrandIcon,
  },
];

const FavoriteIcon = ({ filled = false }) => (
  <HeartIcon size={18} className={filled ? css.favIconFilled : css.favIcon} />
);

const ListingHeader = props => {
  const {
    currentListing,
    isExperienceType,
    currentLanguage,
    countryName,
    city,
    isOwnListing,
    isFavorite,
    onFavoriteClick,
    shareUrl,
    reviews,
    openModalFromOutside,
    photoCount = 0,
  } = props;

  const intl = useIntl();
  const history = useHistory();
  const { lang } = useParams();
  const [isHostModalOpen, setIsHostModalOpen] = useState(false);
  const [shareMenuOpen, setShareMenuOpen] = useState(false);
  const shareMenuRef = useRef(null);

  const publicData = currentListing?.attributes?.publicData || {};
  const { experienceTitleDe, experienceTitle, stayTitle, stayTitleDe } = publicData;
  const reviewStats = getListingReviewStats(currentListing, reviews);
  const reviewsCount = reviewStats.count;
  const ratingValue = reviewStats.rating;
  const showGuestFavorite = ratingValue >= 4.5 && reviewsCount > 0;

  const experienceTitleFinal =
    currentLanguage === 'en'
      ? experienceTitle
      : experienceTitleDe?.length > 0
      ? experienceTitleDe
      : experienceTitle;

  const stayTitleMaybe =
    currentLanguage === 'en' ? stayTitle : stayTitleDe?.length > 0 ? stayTitleDe : stayTitle;

  const listingTitle =
    currentListing?.attributes?.title?.split('• ')?.[0] || currentListing?.attributes?.title || '';

  const finalTitle = isExperienceType
    ? experienceTitleFinal || listingTitle
    : stayTitleMaybe?.length > 0
    ? stayTitleMaybe
    : listingTitle;

  const experienceTypeRaw = currentListing?.attributes?.title?.split('• ')?.[1];
  const experienceDuration = experienceTypeRaw
    ? formatDurationLabel(getExperienceTitle(experienceTypeRaw, intl))
    : null;

  const hostDisplayName = currentListing?.author?.attributes?.profile?.displayName || '';
  const hostFirstName = hostDisplayName.split(' ')[0];
  const hostId = currentListing?.author?.id?.uuid;
  const continent = findContinent(countryName, currentLanguage);
  const continentLabel =
    continent && (currentLanguage === 'de' ? continent.titleDe : continent.titleEn);

  const tagline = getTagline(currentListing, isExperienceType, currentLanguage);

  const formattedRating = intl.formatNumber(ratingValue, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });

  const handleShare = event => {
    event?.stopPropagation();
    setShareMenuOpen(open => !open);
  };

  const handleSharePlatform = async (platform, event) => {
    event?.stopPropagation();
    if (!shareUrl) return;

    const encodedUrl = encodeURIComponent(shareUrl);
    const shareText = encodeURIComponent(`${finalTitle} ${shareUrl}`);

    if (platform === 'wa') {
      window.open(`https://wa.me/?text=${shareText}`, '_blank', 'noopener,noreferrer');
    } else if (platform === 'fb') {
      const shareData = {
        title: finalTitle,
        text: finalTitle,
        url: shareUrl,
      };

      setShareMenuOpen(false);

      if (navigator.share) {
        try {
          await navigator.share(shareData);
        } catch (error) {
          if (error.name !== 'AbortError') {
            console.error('Unable to share listing:', error);
          }
        }
      } else {
        window.open(
          `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
          '_blank',
          'noopener,noreferrer'
        );
      }

      return;
    } else if (platform === 'ig') {
      const shareData = {
        title: finalTitle,
        text: finalTitle,
        url: shareUrl,
      };

      if (navigator.share) {
        try {
          await navigator.share(shareData);
        } catch (error) {
          // Ignore when the user closes the native share dialog.
          if (error.name !== 'AbortError') {
            console.error('Unable to share listing:', error);
          }
        }
      } else if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(shareUrl).catch(() => {});
      }
    }

    setShareMenuOpen(false);
  };

  const handleReviewsClick = event => {
    event?.stopPropagation();
    if (openModalFromOutside) {
      openModalFromOutside(true);
      return;
    }

    const reviewsSection = document.getElementById('listing-reviews-section');
    reviewsSection?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  useEffect(() => {
    if (!shareMenuOpen) return undefined;

    const handleClickOutside = event => {
      if (shareMenuRef.current && !shareMenuRef.current.contains(event.target)) {
        setShareMenuOpen(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [shareMenuOpen]);

  const hostModalContent = (
    <div className={css.modalContentWrapper}>
      <div className={css.modalLeftSection}>
        <AvatarMedium
          className={css.avatarMedium}
          user={currentListing.author}
          disableProfileLink
        />
        <ReviewRatings
          isHostModal
          currentListing={currentListing}
          reviewStats={reviewStats}
          reviews={reviews}
          googleReviews={publicData.location?.reviews}
          rating={publicData.location?.rating}
          onShowModalFromOutside={openModalFromOutside}
          showText
          showDot
          starContainerClass={css.starContainer}
          showReviewsNumber={false}
        />
      </div>
      <div className={css.modalRightSection}>
        <h2>
          <FormattedMessage id="ProfilePage.desktopHeading" values={{ name: hostFirstName }} />
        </h2>
        <p>
          {currentListing.author.attributes.profile.publicData.aboutTheHost ? (
            lang === 'en' ? (
              currentListing.author.attributes.profile.publicData.aboutTheHost
            ) : (
              currentListing.author.attributes.profile.publicData.aboutTheHost_de
            )
          ) : (
            <FormattedMessage
              id="AboutProjectNew.placeholderHost"
              values={{ hostName: hostFirstName }}
            />
          )}
        </p>
        <NamedLinkNewTab name="ProfilePage" params={{ slug: '', id: hostId }}>
          <PrimaryButton className={css.viewProfileButton} onClick={() => history.push()}>
            <FormattedMessage id="UserCard.viewProfileLink" />
          </PrimaryButton>
        </NamedLinkNewTab>
      </div>
    </div>
  );

  return (
    <div className={css.overlay}>
      <nav
        className={`${css.breadcrumb} ${isOwnListing ? css.breadcrumbEditingMode : ''}`}
        aria-label="Breadcrumb"
      >
        <NamedLink name="SearchPage" className={css.breadcrumbLink}>
          <FormattedMessage id="ListingHeader.breadcrumbTravel" />
        </NamedLink>
        {continentLabel && (
          <>
            <span className={css.breadcrumbSep} aria-hidden="true">
              ›
            </span>
            <span className={css.breadcrumbCurrent}>{continentLabel}</span>
          </>
        )}
        {countryName && (
          <>
            <span className={css.breadcrumbSep} aria-hidden="true">
              ›
            </span>
            <span className={css.breadcrumbCurrent}>{countryName}</span>
          </>
        )}
        {city && (
          <>
            <span className={css.breadcrumbSep} aria-hidden="true">
              ›
            </span>
            <span className={css.breadcrumbCurrent}>{city}</span>
          </>
        )}
      </nav>

      <div className={`${css.actions} ${isOwnListing ? css.actionsEditingMode : ''}`}>
        <div className={css.shareWrapper} ref={shareMenuRef}>
          <button
            type="button"
            className={css.actionBtn}
            onClick={handleShare}
            aria-expanded={shareMenuOpen}
            aria-haspopup="true"
          >
            <ShareIcon />
            <FormattedMessage id="ListingHeader.share" />
          </button>
          <button
            type="button"
            className={css.iconBtn}
            onClick={handleShare}
            aria-label={intl.formatMessage({ id: 'ListingHeader.share' })}
            aria-expanded={shareMenuOpen}
            aria-haspopup="true"
          >
            <ShareIcon />
          </button>
          {shareMenuOpen && (
            <div className={css.shareMenu} role="menu">
              {SHARE_OPTIONS.map(({ id, label, Icon }) => (
                <button
                  key={id}
                  type="button"
                  className={css.shareMenuItem}
                  role="menuitem"
                  onClick={event => handleSharePlatform(id, event)}
                  aria-label={label}
                >
                  <Icon className={css.shareMenuIcon} size={22} />
                  <span className={css.shareMenuLabel}>{label}</span>
                </button>
              ))}
            </div>
          )}
        </div>
        <button
          type="button"
          className={css.actionBtn}
          onClick={event => {
            event.stopPropagation();
            onFavoriteClick();
          }}
        >
          <FavoriteIcon filled={isFavorite} />
          <FormattedMessage id="ListingHeader.save" />
        </button>
        <button
          type="button"
          className={css.iconBtn}
          onClick={event => {
            event.stopPropagation();
            onFavoriteClick();
          }}
          aria-label={intl.formatMessage({ id: 'ListingHeader.save' })}
        >
          <FavoriteIcon filled={isFavorite} />
        </button>
      </div>

      <div className={css.heroBottom}>
        <div className={css.badgesRow}>
          {showGuestFavorite && (
            <span className={css.badgeFavorite}>
              <FormattedMessage id="ReviewHighlight.badge" />
            </span>
          )}
          {ratingValue > 0 && reviewsCount > 0 && (
            <>
              <button type="button" className={css.badgeRating} onClick={handleReviewsClick}>
                <StarIcon size={13} className={css.ratingStar} />
                {formattedRating} ·{' '}
                <FormattedMessage id="ListingHeader.reviewCount" values={{ count: reviewsCount }} />
              </button>
              <button type="button" className={css.badgeRatingCompact} onClick={handleReviewsClick}>
                <StarIcon size={13} className={css.ratingStar} />
                {formattedRating} ({reviewsCount})
              </button>
            </>
          )}
          {isExperienceType && (
            <span className={css.badgePackage}>
              <FormattedMessage id="ListingHeader.packageTour" />
            </span>
          )}
        </div>

        <h1 className={css.title}>{finalTitle}</h1>

        {(city || countryName || experienceDuration) && (
          <div className={css.metaLineCompact}>
            {city && countryName && (
              <span>
                {city}, {countryName}
              </span>
            )}
            {isExperienceType && experienceDuration && (
              <span>{city && countryName ? ` · ${experienceDuration}` : experienceDuration}</span>
            )}
          </div>
        )}

        <div className={css.metaLine}>
          {city && countryName && (
            <span className={css.metaItem}>
              <img src={pinIcon} className={css.pinIcon} alt="" />
              {city} · {countryName}
            </span>
          )}
          {isExperienceType && experienceDuration && (
            <>
              <span className={css.metaSep} aria-hidden="true">
                |
              </span>
              <span>{experienceDuration}</span>
            </>
          )}
          {hostDisplayName && (
            <>
              <span className={css.metaSep} aria-hidden="true">
                |
              </span>
              <span className={css.metaItem}>
                <FormattedMessage id="ListingHeader.hostLabel" />{' '}
                <button
                  type="button"
                  className={css.hostName}
                  onClick={event => {
                    event.stopPropagation();
                    setIsHostModalOpen(true);
                  }}
                >
                  {hostFirstName}
                </button>
              </span>
            </>
          )}
        </div>

        {tagline && <p className={css.tagline}>{tagline}</p>}
      </div>

      {photoCount > 1 && (
        <span className={css.photoCounter} onClick={event => event.stopPropagation()}>
          1 / {photoCount}
        </span>
      )}

      {isHostModalOpen && (
        <Modal
          isOpen={isHostModalOpen}
          onClose={() => setIsHostModalOpen(false)}
          isHostModal
          showCloseButtonText={false}
        >
          {hostModalContent}
        </Modal>
      )}
    </div>
  );
};

export default ListingHeader;
